import {
	CAUTION_CR_MULT,
	FALLBACK_LIQUIDATION_THRESHOLD,
	FALLBACK_MIN_COLLATERAL_RATIO,
	RISK_STATUS_COLORS,
	classifyVaultRisk,
	type VaultRiskBand,
} from "@/lib/vault-risk";

/**
 * Offline fallbacks only — prefer live getProtocolParams().
 * MIN_COLLATERAL_RATIO = mint floor (not the soft-band liquidate line).
 * Liquidation threshold is separate (default 1.30×).
 */
export const MIN_COLLATERAL_RATIO = FALLBACK_MIN_COLLATERAL_RATIO;
export const LIQUIDATION_THRESHOLD = FALLBACK_LIQUIDATION_THRESHOLD;

/** Max mint LTV % from min CR: 100 / 1.5 ≈ 66.67 */
export const DISPLAY_MAX_LTV = 100 / MIN_COLLATERAL_RATIO;

/** Max LTV % at a given CR threshold */
export function maxLtvFromCr(cr: number): number {
	if (!(cr > 0)) return DISPLAY_MAX_LTV;
	return 100 / cr;
}

/** Gauge / LTV-bar zones — aliases of VaultRiskBand (minus empty). */
export type RiskZone =
	| "safe"
	| "moderate"
	| "danger"
	| "liquidatable"
	| "underwater";

/** Legacy LTV cutoffs for bar fills — prefer getRiskZoneFromCr for status */
export const RISK_THRESHOLDS = {
	safe: 50,
	moderate: DISPLAY_MAX_LTV * 0.9,
} as const;

export function riskBandToZone(band: VaultRiskBand): RiskZone {
	switch (band) {
		case "healthy":
			return "safe";
		case "caution":
			return "moderate";
		case "at_risk":
			return "danger";
		case "liquidatable":
			return "liquidatable";
		case "underwater":
			return "underwater";
		case "empty":
			return "safe";
	}
}

/**
 * Zone from collateralization ratio — delegates to classifyVaultRisk.
 * - Healthy (safe): CR ≥ mint × CAUTION_CR_MULT
 * - Caution (moderate): mint ≤ CR < mint × CAUTION_CR_MULT
 * - At risk (danger): liqT ≤ CR < mint
 * - Liquidatable: 1 ≤ CR < liqT
 * - Underwater: CR < 1
 */
export function getRiskZoneFromCr(
	cr: number,
	minCr: number = MIN_COLLATERAL_RATIO,
	liqT: number = LIQUIDATION_THRESHOLD,
): RiskZone {
	if (!Number.isFinite(cr) || cr === Number.POSITIVE_INFINITY) return "safe";
	const mint = minCr > 0 ? minCr : MIN_COLLATERAL_RATIO;
	const liq = liqT > 0 ? Math.min(liqT, mint) : LIQUIDATION_THRESHOLD;
	// Reconstruct USD-like inputs so classifyVaultRisk bands match.
	const debt = 1;
	const coll = cr * debt;
	const meta = classifyVaultRisk(coll, debt, {
		minCollateralRatio: mint,
		liquidationThreshold: liq,
	});
	return riskBandToZone(meta.band);
}

/** @deprecated Prefer getRiskZoneFromCr — LTV-based zones confuse mint vs liq lines */
export function getRiskZone(ltv: number, liquidationLtv?: number): RiskZone {
	const liqLtv = liquidationLtv ?? DISPLAY_MAX_LTV;
	if (!(ltv > 0)) return "safe";
	const cr = 100 / ltv;
	const liqCr = 100 / liqLtv;
	const mintCr = 100 / DISPLAY_MAX_LTV;
	return getRiskZoneFromCr(cr, mintCr, liqCr);
}

export function riskZoneColor(zone: RiskZone): string {
	switch (zone) {
		case "safe":
			return RISK_STATUS_COLORS.healthy;
		case "moderate":
			return RISK_STATUS_COLORS.caution;
		case "danger":
			return RISK_STATUS_COLORS.at_risk;
		case "liquidatable":
			return RISK_STATUS_COLORS.liquidatable;
		case "underwater":
			return RISK_STATUS_COLORS.underwater;
	}
}

export function ltvGradient(ltv: number, maxLtv: number): string {
	const pct = Math.min(100, (ltv / maxLtv) * 100);
	if (pct < 60)
		return `linear-gradient(90deg, ${RISK_STATUS_COLORS.healthy}, #10B981)`;
	if (pct < 79)
		return `linear-gradient(90deg, ${RISK_STATUS_COLORS.healthy}, ${RISK_STATUS_COLORS.caution})`;
	return `linear-gradient(90deg, ${RISK_STATUS_COLORS.caution}, ${RISK_STATUS_COLORS.at_risk})`;
}

/**
 * Protocol health factor = CR = collateralUsd / debtUsdm.
 * Same definition as Motoko healthFactorE8.
 */
export function healthFactorFromUsd(
	collateralUsd: number,
	debtUsdm: number,
): number {
	if (!(debtUsdm > 0)) return Number.POSITIVE_INFINITY;
	if (!(collateralUsd > 0)) return 0;
	return collateralUsd / debtUsdm;
}

/**
 * @deprecated Do not use for "Health" display — returns maxMintLtv/ltv, not CR.
 * Prefer healthFactorFromUsd(coll, debt).
 */
export function healthFactorFromLtv(ltvPct: number, maxLtv = DISPLAY_MAX_LTV) {
	if (ltvPct <= 0) return Number.POSITIVE_INFINITY;
	return maxLtv / ltvPct;
}

/** Max USDM mintable: collUsd / minCR */
export function maxMintFromCollateral(
	colUsd: number,
	minCr = MIN_COLLATERAL_RATIO,
): number {
	if (colUsd <= 0 || minCr <= 0) return 0;
	return colUsd / minCr;
}

/**
 * Liquidation price for a single asset deposit (at liq CR).
 * amount * liqPrice = debt * liqCr
 */
export function liquidationPrice(
	debtUsdm: number,
	assetAmount: number,
	liqCr = LIQUIDATION_THRESHOLD,
): number {
	if (assetAmount <= 0 || debtUsdm <= 0) return 0;
	return (debtUsdm * liqCr) / assetAmount;
}

/** Collateralization ratio as multiplier (e.g. 2.0×) — same as health */
export function collateralizationRatio(
	collateralUsd: number,
	debtUsdm: number,
): number {
	return healthFactorFromUsd(collateralUsd, debtUsdm);
}

export { CAUTION_CR_MULT };
